'use client'

import {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
  type FC,
} from 'react'
import { scrollToSection } from '@/lib/utils'
import type { Category, Deal } from '@/payload-types'

export type CategoryFilter = Pick<Category, 'id' | 'name' | 'slug' | 'icon'>

type PageContextType = {
  appFocus: string | undefined | null
  setAppFocus: (focusId: string | undefined | null) => void
  dealFocus: Deal | null
  setDealFocus: (focusId: Deal | null) => void
  setPageTab: (tab: string) => void
  pageTab: string
  selectedFilter: CategoryFilter | null
  setSelectedFilter: (filter: CategoryFilter | null) => void
  selectedCategories: CategoryFilter[] | null
  setSelectedCategories: (filters: CategoryFilter[] | null) => void
  activeSection: string | null
  setActiveSection: (sectionId: string | null) => void
  activeLabel: string | null
  activeApp: string | null
  setActiveApp: (appId: string | null) => void
  sidebarVisible: boolean
  setSidebarVisible: (visible: boolean) => void
  playVideo: boolean
  setPlayVideo: (play: boolean) => void
  query: string
  setQuery: (query: string) => void
  switchFocus: (section: string) => void
}

const PageContext = createContext<PageContextType | undefined>(undefined)

export const usePageContext = () => {
  const context = useContext(PageContext)
  if (!context) {
    throw new Error('usePageContext must be used within a PageContextProvider')
  }
  return context
}

export const useOptionalPageContext = () => {
  return useContext(PageContext) ?? null
}

type PageContextProviderProps = {
  children: ReactNode
  scrollOffset?: number
  initialTab?: string
  // Keeps the URL hash following the scroll-tracked active section, the same
  // way clicking a sidebar link sets it (see BestSidebar's hideHash: false).
  // Opt-in because most pages deliberately hide the hash on click.
}

export const PageContextProvider: FC<PageContextProviderProps> = ({
  children,
  scrollOffset = 160,
  initialTab = '',
}) => {
  const [appFocus, setAppFocus] = useState<string | undefined | null>(undefined)
  const [dealFocus, setDealFocus] = useState<Deal | null>(null)
  const [pageTab, setPageTab] = useState<string>(initialTab)
  const [selectedFilter, setSelectedFilter] = useState<CategoryFilter | null>({
    id: 0,
    name: 'Featured',
    slug: 'featured',
  })
  const [selectedCategories, setSelectedCategories] = useState<CategoryFilter[] | null>(null)
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const [activeLabel, setActiveLabel] = useState<string | null>(null)
  const [activeApp, setActiveApp] = useState<string | null>(null)
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(false)
  const [playVideo, setPlayVideo] = useState<boolean>(false)
  const [query, setQuery] = useState<string>('')

  // Track last scroll position to implement distance-based triggering
  const lastScrollPositionRef = useRef(0)
  const isInitialPageTabMount = useRef(true)
  const scrollThreshold = 10
  const checkScrollRef = useRef<((force?: boolean) => void) | null>(null)

  const activeSectionRef = useRef<string | null>(null)
  const activeAppRef = useRef<string | null>(null)

  useEffect(() => {
    const checkScroll = (force = false) => {
      const currentScrollPosition = window.scrollY

      // Only proceed if scroll moved more than threshold (unless forced)
      if (
        !force &&
        Math.abs(currentScrollPosition - lastScrollPositionRef.current) < scrollThreshold
      ) {
        return
      }

      lastScrollPositionRef.current = currentScrollPosition

      // Helper function to find active element for a given selector
      const findActiveElement = (selector: string) => {
        const elements = document.querySelectorAll<HTMLElement>(selector)
        if (elements.length === 0) return { id: null, name: null }

        let activeId: string | null = null
        let activeDataName: string | null = null
        let closestDistance = Infinity

        elements.forEach((element) => {
          if (element.closest('[data-section-content-visible="false"]')) return

          const elementTop = element.getBoundingClientRect().top
          const elementId = element.dataset.sectionId || element.getAttribute('data-name')

          if (elementId && elementTop <= scrollOffset + 200) {
            const distanceFromTarget = Math.abs(elementTop - scrollOffset)
            if (distanceFromTarget < closestDistance) {
              closestDistance = distanceFromTarget
              activeId = elementId
              activeDataName = element.getAttribute('data-name')
            }
          }
        })

        return { id: activeId, name: activeDataName }
      }

      // Track sections with data-section-id
      const sectionResult = findActiveElement('[data-section-id]')
      if (sectionResult.id !== activeSectionRef.current) {
        activeSectionRef.current = sectionResult.id
        setActiveSection(sectionResult.id)
        setActiveLabel(sectionResult.name)
      }

      // Track app sections with data-section-app
      const appResult = findActiveElement('[data-section-app]')
      if (appResult.id !== activeAppRef.current) {
        activeAppRef.current = appResult.id
        setActiveApp(appResult.id)
      }
    }

    // Throttle with requestAnimationFrame
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          checkScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    checkScroll() // Check initial position
    checkScrollRef.current = checkScroll

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [scrollOffset])

  useEffect(() => {
    const timer = setTimeout(() => {
      checkScrollRef.current?.(true)
    }, 50)

    return () => clearTimeout(timer)
  }, [appFocus, dealFocus])

  useEffect(() => {
    if (dealFocus?.name) {
      setPageTab(dealFocus.name)
    }
  }, [dealFocus])

  useEffect(() => {
    if (appFocus) {
      setPageTab(appFocus)
    } else if (appFocus === null) {
      setPageTab('Comparison')
    }
  }, [appFocus])

  useEffect(() => {
    if (isInitialPageTabMount.current) {
      isInitialPageTabMount.current = false
      return
    }
    if (window.location.hash) {
      const url = new URL(window.location.href)
      url.hash = ''
      window.history.replaceState(null, '', url.toString())
    }
  }, [pageTab])

  const switchFocus = async (section: string) => {
    scrollToSection({ id: 'content', offset: 45, animate: true, hideHash: true })
    setPageTab(section)
  }

  return (
    <PageContext.Provider
      value={{
        appFocus,
        setAppFocus,
        dealFocus,
        setDealFocus,
        pageTab,
        setPageTab,
        selectedFilter,
        setSelectedFilter,
        selectedCategories,
        setSelectedCategories,
        activeSection,
        setActiveSection,
        activeLabel,
        activeApp,
        setActiveApp,
        sidebarVisible,
        setSidebarVisible,
        playVideo,
        setPlayVideo,
        query,
        setQuery,
        switchFocus,
      }}
    >
      {children}
    </PageContext.Provider>
  )
}

export default PageContext
